import { Experimentation } from '@alto/experimentation';
import { Platform } from 'react-native';

export type Brand = 'alto' | 'fuzehealth' | 'fuzerx';

/**
 * Whether a web host is a non-prod FuzeHealth host, which run the rebrand ahead of the cutover so
 * the team can exercise the FuzeRx Auth0 tenant, branding and feature set end to end.
 *
 * A suffix rule rather than a list, so sandboxes (app-<slug>.dev.pharmacy.fuzehealth.com) are
 * covered without anyone remembering to add them.
 *
 * Prod is excluded by construction: the apex pharmacy.fuzehealth.com has no leading dot, and
 * *.prod.pharmacy.fuzehealth.com is filtered out. Until the cutover those patients are still
 * company 34, still in the FuzeHealth Auth0 tenant, and still get the FuzeHealth feature set from
 * disabled_features_by_company. Including prod here is the cutover, and it has to land with the
 * Auth0 user migration, not before.
 *
 * Web only - native returns above this, so the app binary never depends on this list.
 */
const isNonProdFuzeHealthHost = (host: string): boolean =>
  host.endsWith('.pharmacy.fuzehealth.com') && !host.includes('.prod.');

/**
 * Get the brand of the current website. Used to determine the correct colors and other branding assets to load.
 * @returns Brand ('alto', 'fuzehealth', or 'fuzerx')
 */
export const getBrand = (): Brand => {
  if (Experimentation.getFeatureValue('fuze_brand_override', false)) {
    return 'fuzerx';
  }

  if (Platform.OS === 'ios' || Platform.OS === 'android') {
    return 'alto';
  }

  const host = window.location.hostname.toLowerCase();

  if (isNonProdFuzeHealthHost(host)) {
    return 'fuzerx';
  }

  if (host.includes('alto')) {
    return 'alto';
  }

  if (host.includes('fuzehealth')) {
    return 'fuzehealth';
  }

  throw new Error('Unable to resolve brand');
};

/**
 * Whether the current brand is FuzeRx. Use for product decisions that aren't already encoded
 * in a semantic color/font token (e.g. choosing a button's type), not for picking hex values.
 */
export const isFuzeRxBrand = (): boolean => getBrand() === 'fuzerx';
